/**
 * Pure presentation helpers for `/decisions` — the vocabulary labels, the
 * viewer-ballot resolution, the per-round tallies and the create-modal input
 * assembly. Sibling of `pr-board.ts` / `inbox-board.ts`, split out of the
 * page for the same reason: the web tree has no DOM harness, so anything
 * with a branch in it has to live where a unit test can reach it.
 *
 * Every enum label falls back to the raw value — the Postgres vocabularies
 * are append-only, and a member the SPA hasn't shipped a label for must
 * still render as text rather than `undefined`.
 */

import type { DecisionBallot, DecisionRound, StaffDecision } from "./api";

const KIND_LABELS: Readonly<Record<string, string>> = {
  email_correspondent: "Email correspondent",
  ad_hoc: "Ad hoc",
};

/** Human label for a decision kind (falls back to the raw enum value). */
export function decisionKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

const STATUS_LABELS: Readonly<Record<string, string>> = {
  open: "Open",
  resolved: "Resolved",
  cancelled: "Cancelled",
};

/** Human label for a decision status (falls back to the raw enum value). */
export function decisionStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

const QUORUM_LABELS: Readonly<Record<string, string>> = {
  unanimous: "Unanimous",
  majority: "Majority",
};

/** Human label for a quorum rule (falls back to the raw enum value). */
export function quorumLabel(quorum: string): string {
  return QUORUM_LABELS[quorum] ?? quorum;
}

const VOTED_VIA_LABELS: Readonly<Record<string, string>> = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  dashboard: "Dashboard",
};

/** Human label for a vote channel; null stays null (unvoted ballot). */
export function votedViaLabel(via: string | null): string | null {
  if (via === null) return null;
  return VOTED_VIA_LABELS[via] ?? via;
}

/** The decision's CURRENT round, or null when no ballots were issued yet. */
export function currentRound(d: StaffDecision): DecisionRound | null {
  return d.rounds.find((r) => r.round === d.round) ?? null;
}

/**
 * Every round BEFORE the current one, newest first — the display order for
 * the history block (the freshest re-issue is the most relevant).
 */
export function pastRounds(d: StaffDecision): ReadonlyArray<DecisionRound> {
  return d.rounds
    .filter((r) => r.round !== d.round)
    .slice()
    .sort((a, b) => b.round - a.round);
}

/**
 * The signed-in viewer's ballot among `ballots`. The server already marks it
 * — the viewer's own ballot is the ONLY one whose `token` survives the
 * server-side scoping — so a token-bearing ballot wins outright, keeping
 * this match correct even if the server's identity resolution outgrows the
 * displayed email. The case-insensitive email match is the FALLBACK for
 * token-less payloads (server failed closed on an ambiguous shared address,
 * or the login email isn't in the registry at all) — the page then still
 * shows the viewer's voted state, and renders an unvoted round read-only
 * rather than guessing.
 */
export function findViewerBallot(
  ballots: ReadonlyArray<DecisionBallot>,
  viewerEmail: string | null,
): DecisionBallot | null {
  const marked = ballots.find((b) => b.token !== null);
  if (marked !== undefined) return marked;
  if (viewerEmail === null) return null;
  const viewer = viewerEmail.trim().toLowerCase();
  if (viewer.length === 0) return null;
  return (
    ballots.find(
      (b) => b.email !== null && b.email.trim().toLowerCase() === viewer,
    ) ?? null
  );
}

/**
 * Votes per option for one round — `tally[i]` is the number of ballots that
 * chose option `i`. Unvoted ballots and out-of-range indexes (an option
 * vocabulary that shrank, or a corrupt row) are ignored rather than crashing
 * the count.
 */
export function optionTally(
  ballots: ReadonlyArray<DecisionBallot>,
  optionCount: number,
): ReadonlyArray<number> {
  const tally = new Array<number>(Math.max(0, optionCount)).fill(0);
  for (const b of ballots) {
    if (
      b.option_index !== null &&
      Number.isInteger(b.option_index) &&
      b.option_index >= 0 &&
      b.option_index < tally.length
    ) {
      tally[b.option_index] = (tally[b.option_index] ?? 0) + 1;
    }
  }
  return tally;
}

/** How many of a round's ballots have been cast. */
export function votesProgress(ballots: ReadonlyArray<DecisionBallot>): {
  readonly voted: number;
  readonly total: number;
} {
  return {
    voted: ballots.filter((b) => b.voted_at !== null).length,
    total: ballots.length,
  };
}

/**
 * Display label for an option index: the option's label, or `Option N` when
 * the label is blank or the index is outside the vocabulary (never
 * `undefined` in a cell). Null only for a null index (nothing chosen).
 */
export function optionLabelAt(
  d: StaffDecision,
  index: number | null,
): string | null {
  if (index === null) return null;
  const label = d.options[index]?.label ?? "";
  return label.trim().length > 0 ? label : `Option ${index + 1}`;
}

/** The resolved decision's winning-option label, or null while unresolved. */
export function winningOptionLabel(d: StaffDecision): string | null {
  return optionLabelAt(d, d.winning_option_index);
}

/**
 * A staff_id's display name, scanned from the ballot history (the payload
 * carries no separate staff directory). Null for null / unknown ids —
 * `resolved_by` may be an actor no round ever balloted.
 */
export function staffNameById(
  d: StaffDecision,
  staffId: string | null,
): string | null {
  if (staffId === null) return null;
  for (const round of d.rounds) {
    const hit = round.ballots.find((b) => b.staff_id === staffId);
    if (hit !== undefined) return hit.display_name;
  }
  return null;
}

/**
 * Open decisions whose current round holds an UNVOTED ballot for the viewer
 * — the "your vote is needed" overview figure.
 */
export function countAwaitingViewer(
  open: ReadonlyArray<StaffDecision>,
  viewerEmail: string | null,
): number {
  return open.filter((d) => {
    const round = currentRound(d);
    if (round === null) return false;
    const mine = findViewerBallot(round.ballots, viewerEmail);
    return mine !== null && mine.voted_at === null;
  }).length;
}

/**
 * Assemble `create_staff_decision`'s wire input from the New-decision
 * modal's flat fields: `option_1..option_4` (blank optionals are already
 * omitted by the modal) fold into the `options` array of `{label}` objects,
 * order preserved; everything else passes through untouched. Runs as the
 * modal's `transformInput`.
 */
export function buildCreateDecisionInput(
  input: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const options: Array<{ label: string }> = [];
  for (const name of ["option_1", "option_2", "option_3", "option_4"]) {
    const v = input[name];
    if (typeof v === "string" && v.trim().length > 0) {
      options.push({ label: v.trim() });
    }
  }
  for (const [k, v] of Object.entries(input)) {
    if (!/^option_[1-4]$/.test(k)) out[k] = v;
  }
  out["options"] = options;
  return out;
}
